import { fabric } from 'fabric';
import { useCallback } from 'react';
import Vec2 from 'vec2';

import { useDropHandler } from './useDropHandler';
import { useMouseDown } from './useMouseDown';
import { useMouseDownBefore } from './useMouseDownBefore';
import { useMouseUp } from './useMouseUp';
import {
  animateApplianceBack,
  determineMovementType,
  findFabricObjectByName,
  keepApplianceSelected,
  resetDraggingState,
  shouldAnimateBack,
  shouldAutoPanAfterDrag,
  shouldUpdatePosition,
  updateAppliancePosition,
} from './useMouseUp.utils';

import { CANVAS_CURSOR, CANVAS_OBJECT_TYPE_PREFIX } from '~/core/constants';
import { DRAGGING_STATE } from '~/core/enums';
import { DrawingTabEnum } from '~/core/enums/drawing-tab.enum';
import { autoZoomPanToFit } from '~/core/helpers';
import { ApplianceModel, ApplianceType, DrawingShape } from '~/core/models';
import { ApplianceQtyType } from '~/core/types/appliances';
import { isRange } from '~/core/utils';
import {
  useApplianceContext,
  useDrawingShapeContext,
  useFabricCanvasContext,
} from '~/legacy/easy-estimator-page/context';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { increaseCookingAppliance } from '~/redux/slices/cookingAppliance';
import { increaseCustomCutouts } from '~/redux/slices/cutout';
import {
  resetDraggedAppliance,
  setDraggedAppliance,
} from '~/redux/slices/draggedAppliance';
import { updateNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { selectFabricCanvas } from '~/redux/slices/fabric';
import { selectIsManualZoom } from '~/redux/slices/toolbarCanvas';

export const useMouseEvents = () => {
  const dispatch = useAppDispatch();
  const canvas = useAppSelector(selectFabricCanvas);
  const isManualZoom = useAppSelector(selectIsManualZoom);

  const {
    handlers: { updateDrawingShapes },
  } = useDrawingShapeContext();
  const { updateIsDraggingAppliance, updateOnDropAppliance } =
    useApplianceContext();
  const {
    draggingStateRef,
    drawingShapesRef,
    isShapeLogicRef,
    lastPositionRef,
    movingItemRef,
    selectedDrawingTabRef,
    shapeStartDragRef,
  } = useFabricCanvasContext();

  const {
    handleApplianceSelection,
    handleCircleClick,
    handleShapeInteraction,
    startDragging,
  } = useMouseDown(canvas);

  const { handleSelectionCircle, handleApplianceInteraction } =
    useMouseDownBefore(canvas);

  const {
    finalizeDraggingState,
    handleApplianceMovement,
    handleShapeMovement,
    syncShapesToContext,
  } = useMouseUp();

  const { addApplianceToShape, findApplianceAndShape } = useDropHandler(canvas);

  const increaseCooktopAndRange = useCallback(
    (appliance: ApplianceModel) => {
      if (
        ![ApplianceType.COOKTOP, ApplianceType.RANGE].includes(
          appliance.applianceType
        )
      ) {
        return;
      }

      const isApplianceRange = isRange({
        applianceType: appliance.applianceType,
      });

      const selectedType = isApplianceRange
        ? 'selectedRangeApplianceProduct'
        : 'selectedCooktopApplianceProduct';

      dispatch(
        increaseCookingAppliance({
          selectedType,
          applianceId: appliance.productId,
          qtyType: ApplianceQtyType.onShape,
        })
      );
    },
    [dispatch]
  );

  const increaseElectricalAndFaucetCutouts = useCallback(
    (appliance: ApplianceModel) => {
      const cutoutTypes = [
        ApplianceType.ELECTRICAL_CUTOUT,
        ApplianceType.FAUCET_CUTOUT,
      ];

      if (cutoutTypes.includes(appliance.applianceType)) {
        dispatch(
          increaseCustomCutouts({
            cutoutId: appliance.productId,
            qtyType: ApplianceQtyType.onShape,
          })
        );
      }
    },
    [dispatch]
  );

  const createDropHandler = useCallback(
    (args: {
      appliance: ApplianceModel;
      shapeId: DrawingShape['id'];
      centerPoint: Vec2;
      targetShape: DrawingShape | undefined;
      hasChipMinimizer?: boolean;
    }) =>
      () => {
        const {
          appliance,
          shapeId,
          centerPoint,
          targetShape,
          hasChipMinimizer,
        } = args;

        increaseCooktopAndRange(appliance);
        const _shape = drawingShapesRef.current.find(
          (shapeRef) => shapeId === shapeRef.id
        );

        dispatch(resetDraggedAppliance());
        if (!_shape) {
          return; // Shape not found in drawing shapes
        }

        addApplianceToShape({
          appliance,
          shape: _shape,
          centerPoint,
          targetShape,
          hasChipMinimizer,
        });
        updateDrawingShapes([...drawingShapesRef.current]);
        dispatch(updateNeedRedrawCanvas(true));
      },
    [
      increaseCooktopAndRange,
      addApplianceToShape,
      updateDrawingShapes,
      drawingShapesRef,
      dispatch,
    ]
  );

  const onDrop = useCallback(
    ({ e }: fabric.IEvent<MouseEvent | DragEvent>) => {
      e.stopPropagation();
      e.preventDefault();

      updateIsDraggingAppliance(false);

      const applianceAndShape = findApplianceAndShape(e as DragEvent);

      if (!applianceAndShape) {
        return;
      }

      const { appliance, shape, targetShape } = applianceAndShape;

      const centerPoint = new Vec2(canvas.getPointer(e));
      const onDropAppliance = createDropHandler({
        appliance,
        shapeId: shape.id,
        centerPoint,
        targetShape,
      });

      const cutoutTypes = [
        ApplianceType.ELECTRICAL_CUTOUT,
        ApplianceType.FAUCET_CUTOUT,
      ];
      const isCookingAppliance = [
        ApplianceType.COOKTOP,
        ApplianceType.RANGE,
      ].includes(appliance.applianceType);
      const includesCutoutType = cutoutTypes.includes(appliance.applianceType);

      const shouldStoreForLater = !includesCutoutType && !isCookingAppliance;

      if (shouldStoreForLater) {
        dispatch(
          setDraggedAppliance({
            appliance,
            centerPoint: { x: centerPoint.x, y: centerPoint.y },
            targetShape,
            shapeId: shape.id,
          })
        );
        updateOnDropAppliance(onDropAppliance);
      } else {
        increaseElectricalAndFaucetCutouts(appliance);
        onDropAppliance();
      }
    },
    [
      dispatch,
      updateIsDraggingAppliance,
      findApplianceAndShape,
      canvas,
      createDropHandler,
      updateOnDropAppliance,
      increaseElectricalAndFaucetCutouts,
    ]
  );

  const onMouseDownBefore = useCallback(
    ({ target, ...e }: fabric.IEvent<MouseEvent>) => {
      if (!target) {
        return;
      }

      const targetName = target.name;
      const groupMemberClicked = e.subTargets?.[0];
      const pointer = new Vec2(canvas.getPointer(e.e));

      if (targetName?.includes(CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE)) {
        handleSelectionCircle(pointer);
      } else {
        handleApplianceInteraction(
          target,
          groupMemberClicked,
          targetName,
          pointer
        );
      }
    },
    [canvas, handleSelectionCircle, handleApplianceInteraction]
  );

  const onMouseDown = useCallback(
    ({ target, ...e }: fabric.IEvent<MouseEvent>) => {
      if (target) {
        const targetName = target.name;
        const pointer = new Vec2(canvas.getPointer(e.e));

        if (targetName?.includes(CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE)) {
          handleApplianceSelection(targetName, pointer);
        } else if (
          targetName?.includes(CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE)
        ) {
          handleCircleClick(target);
        } else if (targetName && isShapeLogicRef.current) {
          handleShapeInteraction(targetName, pointer);
        } else if (
          selectedDrawingTabRef.current !== DrawingTabEnum.FREE_DRAWING_TAB
        ) {
          startDragging(e.e);
        }
      }
    },
    [
      canvas,
      handleApplianceSelection,
      handleCircleClick,
      handleShapeInteraction,
      isShapeLogicRef,
      startDragging,
      selectedDrawingTabRef,
    ]
  );

  const onMouseMove = useCallback(
    (e: fabric.IEvent<MouseEvent>) => {
      if (draggingStateRef.current !== DRAGGING_STATE.DRAGGING) {
        return;
      }

      if (!e || !e.e) {
        return;
      }

      // Calculate deltas - manually calculated for cross-browser compatibility
      const deltaX = lastPositionRef.current.x
        ? e.e.clientX - lastPositionRef.current.x
        : 0;
      const deltaY = lastPositionRef.current.y
        ? e.e.clientY - lastPositionRef.current.y
        : 0;
      const delta = new fabric.Point(deltaX, deltaY);

      // Update the last X and Y values
      lastPositionRef.current = { x: e.e.clientX, y: e.e.clientY };

      // Canvas dragging: when draggingState is DRAGGING but movingItemRef is not set
      // This means we're dragging the canvas itself, not a shape or appliance
      if (!movingItemRef.current) {
        canvas.relativePan(delta);
        canvas.defaultCursor = CANVAS_CURSOR.DEFAULT.GRABBING;

        return;
      }

      // Shape or appliance dragging - pan canvas to allow dragging objects near viewport edges
      canvas.relativePan(delta);
      canvas.defaultCursor = CANVAS_CURSOR.DEFAULT.GRABBING;
    },
    [canvas, draggingStateRef, lastPositionRef, movingItemRef]
  );

  const onMouseUp = useCallback(
    (e: fabric.IEvent<MouseEvent>) => {
      // Handle canvas auto-pan in auto-zoom mode
      if (
        shouldAutoPanAfterDrag(
          draggingStateRef.current,
          movingItemRef.current,
          isManualZoom
        )
      ) {
        autoZoomPanToFit(canvas, false, true);
      }

      // Handle movement completion for shapes and appliances
      if (movingItemRef.current) {
        const pointer = new Vec2(canvas.getPointer(e.e));
        const moveVec = shapeStartDragRef.current.subtract(pointer, true);
        const movementType = determineMovementType({
          movingItem: movingItemRef.current,
          moveVec,
        });

        switch (movementType) {
          case 'shape':
            // Shape movement - triggers full canvas redraw
            handleShapeMovement(moveVec);
            finalizeDraggingState();
            break;

          case 'appliance-moved': {
            // Appliance movement - incremental update without full redraw
            // CRITICAL: Do NOT call finalizeDraggingState() - causes race condition
            const applianceId = movingItemRef.current as string;
            const result = handleApplianceMovement(moveVec);
            const fabricObj = findFabricObjectByName(canvas, applianceId);

            if (shouldAnimateBack(fabricObj, result)) {
              animateApplianceBack({ fabricObj, result, canvas });
            } else if (
              shouldUpdatePosition(fabricObj, result) &&
              result.appliance
            ) {
              updateAppliancePosition({
                fabricObj,
                appliance: result.appliance,
              });
            }

            // Persist to React state - DO NOT use finalizeDraggingState here
            syncShapesToContext();
            keepApplianceSelected(canvas, fabricObj);
            break;
          }

          case 'appliance-clicked':
            // Appliance was clicked but not moved - just reset state below
            // DO NOT call finalizeDraggingState() - triggers unnecessary redraw
            break;

          // 'canvas' case is handled above (auto-pan check)
        }
      }

      // ALWAYS reset state at the end - this is critical for proper state cleanup
      resetDraggingState({
        draggingStateRef,
        movingItemRef,
        canvas,
      });
    },
    [
      canvas,
      draggingStateRef,
      finalizeDraggingState,
      handleApplianceMovement,
      handleShapeMovement,
      isManualZoom,
      movingItemRef,
      shapeStartDragRef,
      syncShapesToContext,
    ]
  );

  return {
    onDrop,
    onMouseDownBefore,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    createDropHandler,
  };
};
